import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import type { KanbanColumn } from '@/shared/types'

interface TimelineEntry {
  id: string
  from_column: KanbanColumn | null
  to_column: KanbanColumn
  changed_at: string
}

interface StatusTimelineProps {
  entries: TimelineEntry[]
  createdAt: string
}

const columnColors: Record<KanbanColumn, string> = {
  backlog: 'bg-slate-500',
  scoping: 'bg-violet-600',
  design: 'bg-pink-500',
  development: 'bg-blue-500',
  testing: 'bg-orange-500',
  done: 'bg-green-500',
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function formatDuration(ms: number): string {
  const hours = Math.floor(ms / (1000 * 60 * 60))
  const days = Math.floor(hours / 24)

  if (days > 0) {
    return `${days}d ${hours % 24}h`
  }
  if (hours > 0) {
    return `${hours}h`
  }
  const minutes = Math.floor(ms / (1000 * 60))
  return `${minutes}m`
}

export function StatusTimeline({ entries, createdAt }: StatusTimelineProps) {
  const { t } = useTranslation()

  // Build timeline items: start with creation, then transitions
  const timelineItems: Array<{
    id: string
    column: KanbanColumn
    date: string
    duration?: string
    isFirst: boolean
    isLast: boolean
  }> = []

  // First item: creation (always backlog)
  timelineItems.push({
    id: 'created',
    column: 'backlog',
    date: createdAt,
    isFirst: true,
    isLast: entries.length === 0,
  })

  // Add transitions
  entries.forEach((entry, index) => {
    // Calculate duration from previous state
    const prevDate = index === 0 ? createdAt : entries[index - 1].changed_at
    const duration = formatDuration(new Date(entry.changed_at).getTime() - new Date(prevDate).getTime())

    timelineItems.push({
      id: entry.id,
      column: entry.to_column,
      date: entry.changed_at,
      duration,
      isFirst: false,
      isLast: index === entries.length - 1,
    })
  })

  return (
    <div className="flex flex-col">
      {timelineItems.map((item) => (
        <div key={item.id} className="flex gap-4">
          {/* Timeline line and dot */}
          <div className="flex flex-col items-center">
            {/* Dot */}
            <div
              className={cn(
                'h-3 w-3 rounded-full border-2 border-white shadow-sm',
                columnColors[item.column],
                item.isLast && 'ring-2 ring-offset-2',
                item.isLast && item.column === 'done' ? 'ring-green-300' : item.isLast && 'ring-slate-300'
              )}
            />
            {/* Line (not on last item) */}
            {!item.isLast && (
              <div className="h-10 w-0.5 bg-border" />
            )}
          </div>

          {/* Content */}
          <div className={cn('flex flex-col gap-0.5 pb-4', item.isLast && 'pb-0')}>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'rounded-md px-2 py-0.5 text-xs font-medium text-white',
                  columnColors[item.column]
                )}
              >
                {t(`kanban.columns.${item.column}`)}
              </span>
              {item.duration && (
                <span className="text-xs text-foreground-muted">
                  +{item.duration}
                </span>
              )}
            </div>
            <span className="text-xs text-foreground-muted">
              {formatDate(item.date)}
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}
